import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router";
import { authSelector } from "../../redux/selectors/auth.selector";
import { GeneralForListSchema, L003Schema, Result } from "../../generated";
import { fetchGeneralsForList, fetchOrder, putChangeStatus, deleteOrderDetail } from "../../services/api.service";
import LoadingSpinner from "../../components/Loading/LoadingSpinner";
import Title from "../../components/Title/Title";
import PaginationTable from "../../components/PaginationTable/PaginationTable";
import { useLoadingIndicator } from "../../hooks/LoadingIndicator.hooks";
import EquipmentPict from "../../components/Pict/EquipmentPict";
import SelectForTable, { COLOR_CLASS, SelectStatusInfo } from "../../components/Select/SelectForTable";
import ConfirmModal from "../../components/ConfirmModal";
import { notify, notifyError } from "../../helper/settings.helper";
import { useAuth } from "../../helper/auth.helper";
import { HEADQUARTER_USER } from "../../constants";
import Status, { STATUS_BLUE, STATUS_GREEN, STATUS_RED, StatusSchema } from "../../components/Status/Status";
import { DELIVERY_STATUS, ORDER_DETAIL_DELIVERY_STATUS_ARRIVED } from "../../models/equipment.model";
import { getYyyyMmDd } from "../../helper/date.helper";
import Delete from "../../components/Icon/Delete";
import { useHistory } from "react-router";

interface iL003 {
  order_id: number;
}

const L003 = () => {
  const { checkLoggedIn } = useAuth();
  const authState = useSelector(authSelector);
  const location = useLocation<iL003>();
  const orderId = location.state.order_id;
  const [isLoading, _changeLoadingIndicator, startLoading, stopLoading] = useLoadingIndicator();
  const [data, setData] = useState<L003Schema[]>([]);
  const [showRegister, setShowRegister] = useState(false);
  const [statusForSelect, setStatusForSelect] = useState<GeneralForListSchema[]>([]);

  const [changeStatus, setChangeStatus] = useState({
    id: 0,
    status: 0,
  });
  const selectStatusList: SelectStatusInfo[] = statusForSelect.map((x, i) => {
    return { value: x.value, label: x.label, color: [COLOR_CLASS.RED, COLOR_CLASS.GREEN, COLOR_CLASS.BLUE][i] }
  });
  const labelStatusList: StatusSchema[] = selectStatusList.map((x, i) => {
    return { num: x.value, class: [STATUS_RED, STATUS_GREEN, STATUS_BLUE][i] }
  });
  const [reload, setReload] = useState(true);
  const [showDelete, setShowDelete] = useState(false);
  const history = useHistory();
  const [deleteOrderDetailId, setDeleteOrderDetailId] = useState(0);

  const clickRegister = async () => {
    startLoading();
    try {
      const res = await putChangeStatus(changeStatus.id, changeStatus.status, authState);
      if (res.data.result) {
        const targetIndex = data.findIndex(record => record.id === changeStatus.id);
        if (data[targetIndex].delivery_status !== changeStatus.status) {
          setData(prevData => {
            const updateData = [...prevData];
            updateData[targetIndex].delivery_status = changeStatus.status;
            return updateData;
          });
        }
        setShowRegister(false);
        notify("発送ステータスを更新しました。");
      } else {
        notifyError(String(res.data.msg));
        setShowRegister(false);
      }
    } catch (err) {
      checkLoggedIn(err);
      notifyError("登録に失敗しました。");
      console.error(err);
    } finally {
      stopLoading();
    }
  };

  useEffect(() => {
    if (reload) {
      let isMounted = true;
      const fetchData = async () => {
        startLoading();
        try {
          const res = await fetchOrder(orderId, authState);
          if (isMounted) {
            setData(res.data);
            setReload(false);
          }
        } catch (err) {
          if (isMounted) {
            history.goBack();
            console.error(err);
          }
        } finally {
          if (isMounted) {
            stopLoading();
          }
        }
      };
      fetchData();
      return () => {
        isMounted = false;
      };
    }
  }, [authState, checkLoggedIn, orderId, reload, history, startLoading, stopLoading]);

  const clickDelete = async () => {
    startLoading();
    try {
      await deleteOrderDetail(deleteOrderDetailId, authState);
      setShowDelete(false);
      notify("注文を削除しました。");
      setReload(true);
    } catch (err) {
      checkLoggedIn(err);
      notifyError("削除に失敗しました。");
      console.error(err);
    } finally {
      stopLoading();
    }
  };

  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      startLoading();
      try {
        const res = await fetchGeneralsForList(authState, DELIVERY_STATUS);
        if (isMounted) {
          setStatusForSelect(res.data);
        }
      } catch (err) {
        if (isMounted) {
          checkLoggedIn(err);
          notifyError("データの取得に失敗しました。");
          console.error(err);
        }
      } finally {
        if (isMounted) {
          stopLoading();
        }
      }
    };
    if (statusForSelect.length == 0) {
      fetchData();
    }
    return () => {
      isMounted = false;
    }
  }, [authState, checkLoggedIn, startLoading, stopLoading, statusForSelect.length]);

  return (
    <>
      <LoadingSpinner isLoading={isLoading} />
      <Title title={authState.userInfo?.user_type === HEADQUARTER_USER ? "受注詳細" : "発注詳細"} />
      <PaginationTable
        limit={9}
        tr_select={false}
        headers={authState.userInfo?.user_type === HEADQUARTER_USER ?
           ["施設", "画像", "備品名", "品番", "大きさ", "色", "合計金額", "数量", "発注日", "ステータス",""]
            : 
            ["施設", "画像", "備品名", "品番", "大きさ", "色", "合計金額", "数量", "発注日", "ステータス"]
          }
        list={data.map((x, i) => (
          <tr key={i}>
            <td className="item">{x.facility_name}</td>
            <td className="item"><EquipmentPict url={x.image_url} /></td>
            <td className="item hide_over" style={{ maxWidth: "150px" }}>{x.name}</td>
            <td className="item hide_over" style={{ maxWidth: "100px" }}>{x.serial}</td>
            <td className="item hide_over" style={{ maxWidth: "100px" }}>{x.size || "設定なし"}</td>
            <td className="item hide_over" style={{ maxWidth: "100px" }}>{x.color || "設定なし"}</td>
            <td className="item">{x.price.toLocaleString() + "円"}</td>
            <td className="item">{x.num}</td>
            <td className="item">{getYyyyMmDd(new Date(x.order_date))}</td>
            {authState.userInfo?.user_type === HEADQUARTER_USER ?
              <>
                <td className="item" valign="middle" align="center">
                    <SelectForTable
                      status={x.delivery_status}
                      options={selectStatusList}
                      onChange={(e: any) => {
                        setChangeStatus({ id: x.id, status: e.value });
                        setShowRegister(true);
                      }}
                    />
                </td>
                {x.delivery_status !== ORDER_DETAIL_DELIVERY_STATUS_ARRIVED ?
                  <td className="item">
                    <Delete func={(e: any) => {
                      setDeleteOrderDetailId(x.id);
                      setShowDelete(true);
                    }} />
                  </td>
                  :
                  <td className="item"></td>
                }
              </>
            : 
              <td className="item" valign="middle" align="center">
                <Status
                label_list={statusForSelect}
                color_list={labelStatusList}
                status={x.delivery_status}
                max_width={150}
              />
              </td>
            }
          </tr>
        ))}
      />
      <ConfirmModal
        target="登録"
        show={showRegister}
        setShow={setShowRegister}
        func={clickRegister}
        confirmText={"ステータスを変更してよろしいでしょうか。"}
      />
      <ConfirmModal
        target="削除"
        show={showDelete}
        setShow={setShowDelete}
        func={clickDelete}
        confirmText={"注文を削除してよろしいでしょうか。"}
      />
    </>
  );
};

export default L003;
